import type {
  AnyColumnConfig,
  Column,
  ColumnConfig,
  ColumnDataNativeMap,
  ColumnDataType,
  ColumnOption,
  CustomNumber,
  ElementType,
  FilterModel,
  FiltersState,
  FilterStrategy,
  LocationFilterComponentProps,
  TAccessorFn,
  TOrderFn,
  TTransformOptionFn,
} from './types'

import type { TableColumnGroup } from '../../table/tanstack-table'
import { isAnyOf, uniq } from '../lib/array'
import { isColumnOptionArray, isStringArray } from '../lib/helpers'
import { memo } from '../lib/memo'

type ColumnConfigDraft<TData> = Partial<AnyColumnConfig<TData>> & { type: ColumnDataType }

// Update the helper interface
interface FluentColumnConfigHelper<TData> {
  amount: () => ColumnConfigBuilder<TData, 'amount', number>
  company: () => ColumnConfigBuilder<TData, 'company', { label: string; value: string }>
  date: () => ColumnConfigBuilder<TData, 'date', Date>
  location: () => ColumnConfigBuilder<TData, 'location', { label: string; value: string }>
  multiOption: () => ColumnConfigBuilder<TData, 'multiOption', string[]>
  number: () => ColumnConfigBuilder<TData, 'number', number>
  option: () => ColumnConfigBuilder<TData, 'option', string>
  text: () => ColumnConfigBuilder<TData, 'text', string>
}

class ColumnConfigBuilder<
  TData,
  TType extends ColumnDataType = ColumnDataType,
  TVal = unknown,
  TId extends string = string,
> {
  // The draft stores values in their erased form; TType, TVal and TId track them for build().
  private readonly config: ColumnConfigDraft<TData>

  constructor(config: ColumnConfigDraft<TData>) {
    this.config = config
  }

  accessor<TNewVal>(
    accessor: TAccessorFn<TData, TNewVal>,
  ): ColumnConfigBuilder<TData, TType, TNewVal, TId> {
    return new ColumnConfigBuilder({ ...this.config, accessor })
  }

  build(): ColumnConfig<TData, TType, TVal, TId> {
    if (!this.config.id) throw new Error('id is required')
    if (!this.config.accessor) throw new Error('accessor is required')
    if (!this.config.displayName) throw new Error('displayName is required')
    if (this.config.type === 'location' && !this.config.filterComponent) {
      throw new Error('filterComponent is required for location columns')
    }
    if (this.config.type === 'company' && !this.config.filterComponent) {
      throw new Error('filterComponent is required for company columns')
    }
    // SAFETY: each setter only accepts values typed by this builder's TType, TVal and TId, and
    // type-specific setters check `type` at runtime; the required fields are checked above.
    return this.config as ColumnConfig<TData, TType, TVal, TId>
  }

  customNumber(
    value: CustomNumber,
  ): ColumnConfigBuilder<TData, TType extends 'option' ? TType : never, TVal, TId> {
    if (this.config.type !== 'option') {
      throw new Error('customNumber() is only applicable to option columns')
    }
    return new ColumnConfigBuilder({ ...this.config, customNumber: value })
  }

  dayCounts(): ColumnConfigBuilder<TData, TType extends 'date' ? TType : never, TVal, TId> {
    if (this.config.type !== 'date') {
      throw new Error('dayCounts() is only applicable to date columns')
    }
    return new ColumnConfigBuilder({ ...this.config, dayCounts: true })
  }

  displayName(value: string): ColumnConfigBuilder<TData, TType, TVal, TId> {
    return new ColumnConfigBuilder({ ...this.config, displayName: value })
  }

  filterComponent(
    Component: React.ComponentType<LocationFilterComponentProps<ColumnDataNativeMap[TType] | null>>,
  ): ColumnConfigBuilder<TData, TType, TVal, TId> {
    return new ColumnConfigBuilder({ ...this.config, filterComponent: Component })
  }

  group(value: TableColumnGroup): ColumnConfigBuilder<TData, TType, TVal, TId> {
    return new ColumnConfigBuilder({ ...this.config, group: value })
  }

  id<TNewId extends string>(value: TNewId): ColumnConfigBuilder<TData, TType, TVal, TNewId> {
    return new ColumnConfigBuilder({ ...this.config, id: value })
  }

  max(
    value: number,
  ): ColumnConfigBuilder<TData, TType extends 'number' ? TType : never, TVal, TId> {
    if (this.config.type !== 'number') {
      throw new Error('max() is only applicable to number columns')
    }
    return new ColumnConfigBuilder({ ...this.config, max: value })
  }

  min(
    value: number,
  ): ColumnConfigBuilder<TData, TType extends 'number' ? TType : never, TVal, TId> {
    if (this.config.type !== 'number') {
      throw new Error('min() is only applicable to number columns')
    }
    return new ColumnConfigBuilder({ ...this.config, min: value })
  }

  options(
    value: ColumnOption[],
  ): ColumnConfigBuilder<TData, TType extends 'multiOption' | 'option' ? TType : never, TVal, TId> {
    if (!isAnyOf(this.config.type, ['option', 'multiOption'])) {
      throw new Error('options() is only applicable to option or multiOption columns')
    }
    return new ColumnConfigBuilder({ ...this.config, options: value })
  }

  orderFn(
    fn: TOrderFn<TVal>,
  ): ColumnConfigBuilder<TData, TType extends 'multiOption' | 'option' ? TType : never, TVal, TId> {
    if (!isAnyOf(this.config.type, ['option', 'multiOption'])) {
      throw new Error('orderFn() is only applicable to option or multiOption columns')
    }
    return new ColumnConfigBuilder({ ...this.config, orderFn: fn })
  }

  overdueChoices(): ColumnConfigBuilder<TData, TType extends 'date' ? TType : never, TVal, TId> {
    if (this.config.type !== 'date') {
      throw new Error('overdueChoices() is only applicable to date columns')
    }
    return new ColumnConfigBuilder({ ...this.config, overdueChoices: true })
  }

  selectAllNone(
    noneValue: string,
  ): ColumnConfigBuilder<TData, TType extends 'multiOption' ? TType : never, TVal, TId> {
    if (this.config.type !== 'multiOption') {
      throw new Error('selectAllNone() is only applicable to multiOption columns')
    }
    return new ColumnConfigBuilder({ ...this.config, selectAllNone: { noneValue } })
  }

  showOperator(): ColumnConfigBuilder<TData, TType, TVal, TId> {
    return new ColumnConfigBuilder({ ...this.config, showOperator: true })
  }

  transformOptionFn(
    fn: TTransformOptionFn<TVal>,
  ): ColumnConfigBuilder<TData, TType extends 'multiOption' | 'option' ? TType : never, TVal, TId> {
    if (!isAnyOf(this.config.type, ['option', 'multiOption'])) {
      throw new Error('transformOptionFn() is only applicable to option or multiOption columns')
    }
    return new ColumnConfigBuilder({ ...this.config, transformOptionFn: fn })
  }

  unit(
    value: string,
  ): ColumnConfigBuilder<TData, TType extends 'number' ? TType : never, TVal, TId> {
    if (this.config.type !== 'number') {
      throw new Error('unit() is only applicable to number columns')
    }
    return new ColumnConfigBuilder({ ...this.config, unit: value })
  }
}

// Factory function remains mostly the same
export function createColumnConfigHelper<TData>(): FluentColumnConfigHelper<TData> {
  return {
    amount: () => new ColumnConfigBuilder<TData, 'amount', number>({ type: 'amount' }),
    company: () =>
      new ColumnConfigBuilder<TData, 'company', { label: string; value: string }>({
        type: 'company',
      }),
    date: () => new ColumnConfigBuilder<TData, 'date', Date>({ type: 'date' }),
    location: () =>
      new ColumnConfigBuilder<TData, 'location', { label: string; value: string }>({
        type: 'location',
      }),
    multiOption: () =>
      new ColumnConfigBuilder<TData, 'multiOption', string[]>({ type: 'multiOption' }),
    number: () => new ColumnConfigBuilder<TData, 'number', number>({ type: 'number' }),
    option: () => new ColumnConfigBuilder<TData, 'option', string>({ type: 'option' }),
    text: () => new ColumnConfigBuilder<TData, 'text', string>({ type: 'text' }),
  }
}

export function createColumns<TData>(
  data: TData[],
  columnConfigs: ReadonlyArray<AnyColumnConfig<TData>>,
  strategy: FilterStrategy,
): Column<TData>[] {
  return columnConfigs.map(anyColumnConfig => {
    // SAFETY: a config's callbacks are only called with values produced by its own accessor, which
    // is the contract `Column<TData>` exposes with its value type erased to `unknown`.
    const columnConfig = anyColumnConfig as ColumnConfig<TData>
    const getOptions: () => ColumnOption[] = memo(
      () => [data, strategy, columnConfig.options],
      ([data, strategy]) => getColumnOptions(columnConfig, data, strategy),
      { key: `options-${columnConfig.id}` },
    )

    const getValues: Column<TData>['getValues'] = memo(
      () => [data, strategy],
      () => (strategy === 'client' ? getColumnValues(columnConfig, data) : []),
      { key: `values-${columnConfig.id}` },
    )

    const getUniqueValues: () => Map<string, number> | undefined = memo(
      () => [getValues(), strategy],
      ([values, strategy]) => getFacetedUniqueValues(columnConfig, values, strategy),
      { key: `faceted-${columnConfig.id}` },
    )

    const getMinMaxValues: () => [number, number] | undefined = memo(
      () => [data, strategy],
      () => getFacetedMinMaxValues(columnConfig, data, strategy),
      { key: `minmax-${columnConfig.id}` },
    )

    // Create the Column instance
    const column: Column<TData> = {
      ...columnConfig,
      _prefetchedFacetedMinMaxValuesCache: null,
      _prefetchedFacetedUniqueValuesCache: null,
      _prefetchedOptionsCache: null, // Initialize private cache
      _prefetchedValuesCache: null,
      getFacetedMinMaxValues: getMinMaxValues,
      getFacetedUniqueValues: getUniqueValues,
      getOptions,
      getValues,
      prefetchFacetedMinMaxValues: async () => {},
      prefetchFacetedUniqueValues: async () => {},
      // Prefetch methods will be added below
      prefetchOptions: async () => {}, // Placeholder, defined below
      prefetchValues: async () => {},
    }

    if (strategy === 'client') {
      // Define prefetch methods with access to the column instance
      column.prefetchOptions = async (): Promise<void> => {
        if (!column._prefetchedOptionsCache) {
          await new Promise(resolve =>
            setTimeout(() => {
              column._prefetchedOptionsCache = getOptions()
              // console.log(`Prefetched options for ${columnConfig.id}`)
              resolve(undefined)
            }, 0),
          )
        }
      }

      column.prefetchValues = async (): Promise<void> => {
        if (!column._prefetchedValuesCache) {
          await new Promise(resolve =>
            setTimeout(() => {
              column._prefetchedValuesCache = getValues()
              // console.log(`Prefetched values for ${columnConfig.id}`)
              resolve(undefined)
            }, 0),
          )
        }
      }

      column.prefetchFacetedUniqueValues = async (): Promise<void> => {
        if (!column._prefetchedFacetedUniqueValuesCache) {
          await new Promise(resolve =>
            setTimeout(() => {
              const facetedMap = getUniqueValues()
              column._prefetchedFacetedUniqueValuesCache = facetedMap ?? null
              // console.log(
              //   `Prefetched faceted unique values for ${columnConfig.id}`,
              // )
              resolve(undefined)
            }, 0),
          )
        }
      }

      column.prefetchFacetedMinMaxValues = async (): Promise<void> => {
        if (!column._prefetchedFacetedMinMaxValuesCache) {
          await new Promise(resolve =>
            setTimeout(() => {
              const value = getMinMaxValues()
              column._prefetchedFacetedMinMaxValuesCache = value ?? null
              // console.log(
              //   `Prefetched faceted min/max values for ${columnConfig.id}`,
              // )
              resolve(undefined)
            }, 0),
          )
        }
      }
    }

    return column
  })
}

/* Finds the filter on `column`; checking its type narrows the result to that column type. */
export function findFilter<TType extends ColumnDataType>(
  filters: FiltersState,
  column: { id: string; type: TType },
) {
  return filters.find(
    (filter): filter is FilterModel<TType> =>
      filter.columnId === column.id && filter.type === column.type,
  )
}

export function getColumnOptions<TData, TType extends ColumnDataType, TVal>(
  column: ColumnConfig<TData, TType, TVal>,
  data: TData[],
  strategy: FilterStrategy,
): ColumnOption[] {
  // An amount column's options are the currencies offered beside the amount; they are never
  // derived from row data.
  if (column.type === 'amount') {
    if (!column.options) {
      throw new Error(`[data-table-filter] [${column.id}] currency options are required`)
    }
    return column.options
  }

  if (!isAnyOf(column.type, ['option', 'multiOption'])) {
    console.warn('Column options can only be retrieved for option and multiOption columns')
    return []
  }

  if (strategy === 'server' && !column.options) {
    throw new Error('column options are required for server-side filtering')
  }

  if (column.options) {
    return column.options
  }

  const filtered = data
    .flatMap(column.accessor)
    .filter((v): v is NonNullable<TVal> => v !== undefined && v !== null)

  let models = uniq(filtered)

  if (column.orderFn) {
    // SAFETY: flatMap flattened array values one level and nullish values were dropped, so each
    // model is a non-null element of TVal.
    models = models.sort((m1, m2) =>
      column.orderFn!(m1 as ElementType<NonNullable<TVal>>, m2 as ElementType<NonNullable<TVal>>),
    )
  }

  if (column.transformOptionFn) {
    // Memoize transformOptionFn calls
    const memoizedTransform = memo(
      () => [models],
      // SAFETY: each model is a non-null element of TVal, as for orderFn above.
      deps => deps[0].map(m => column.transformOptionFn!(m as ElementType<NonNullable<TVal>>)),
      { key: `transform-${column.id}` },
    )
    return memoizedTransform()
  }

  if (isColumnOptionArray(models)) return models

  throw new Error(
    `[data-table-filter] [${column.id}] Either provide static options, a transformOptionFn, or ensure the column data conforms to ColumnOption type`,
  )
}

export function getColumnValues<TData, TType extends ColumnDataType, TVal>(
  column: ColumnConfig<TData, TType, TVal>,
  data: TData[],
) {
  // Memoize accessor calls
  const memoizedAccessor = memo(
    () => [data],
    // SAFETY: flatMap flattens array values one level and the filter drops nullish values, so each
    // value is a non-null element of TVal.
    deps =>
      deps[0]
        .flatMap(column.accessor)
        .filter((v): v is NonNullable<TVal> => v !== undefined && v !== null) as ElementType<
        NonNullable<TVal>
      >[],
    { key: `accessor-${column.id}` },
  )

  const raw = memoizedAccessor()

  if (!isAnyOf(column.type, ['option', 'multiOption'])) {
    return raw
  }

  if (column.options) {
    return raw
      .map(v => column.options?.find(o => o.value === v)?.value)
      .filter(v => v !== undefined && v !== null)
  }

  if (column.transformOptionFn) {
    const memoizedTransform = memo(
      () => [raw],
      deps => deps[0].map(v => column.transformOptionFn!(v) as ElementType<NonNullable<TVal>>),
      { key: `transform-values-${column.id}` },
    )
    return memoizedTransform()
  }

  if (isColumnOptionArray(raw)) {
    return raw
  }

  throw new Error(
    `[data-table-filter] [${column.id}] Either provide static options, a transformOptionFn, or ensure the column data conforms to ColumnOption type`,
  )
}

export function getFacetedMinMaxValues<TData, TType extends ColumnDataType, TVal>(
  column: ColumnConfig<TData, TType, TVal>,
  data: TData[],
  strategy: FilterStrategy,
): [number, number] | undefined {
  if (column.type !== 'number') return undefined // Only applicable to number columns

  if (typeof column.min === 'number' && typeof column.max === 'number') {
    return [column.min, column.max]
  }

  if (strategy === 'server') {
    return undefined
  }

  const values = data
    .flatMap<unknown>(row => column.accessor(row))
    .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v))

  if (values.length === 0) {
    return [0, 0] // Fallback to config or reasonable defaults
  }

  const min = Math.min(...values)
  const max = Math.max(...values)

  return [min, max]
}

export function getFacetedUniqueValues<TData, TType extends ColumnDataType, TVal>(
  column: ColumnConfig<TData, TType, TVal>,
  values: unknown[],
  strategy: FilterStrategy,
): Map<string, number> | undefined {
  if (!isAnyOf(column.type, ['option', 'multiOption'])) {
    console.warn('Faceted unique values can only be retrieved for option and multiOption columns')
    return new Map<string, number>()
  }

  if (strategy === 'server') {
    return column.facetedOptions
  }

  const acc = new Map<string, number>()

  if (isColumnOptionArray(values)) {
    for (const option of values) {
      const curr = acc.get(option.value) ?? 0
      acc.set(option.value, curr + 1)
    }
  } else if (isStringArray(values)) {
    for (const option of values) {
      const curr = acc.get(option) ?? 0
      acc.set(option, curr + 1)
    }
  }

  return acc
}

/* Checks the column type, narrowing its type-specific config (options, min, filterComponent). */
export function isColumnOfType<TData, TType extends ColumnDataType>(
  column: Column<TData>,
  type: TType,
): column is Column<TData, TType> {
  return column.type === type
}
